<form [formGroup]="emailForm" (submit)="saveEmailSettings()">
	<div class="col-12 text-right">
		<button mat-stroked-button class="mt-2">
			<mat-icon>save</mat-icon> Save
		</button>
	</div>

	<p class="text-muted small mb-3">
		How the ERP sends outgoing mail. Pick a sending method, then fill in the mailbox connection.
		Reading and parsing incoming mail is a separate feature on the <strong>Business mail intake</strong> tab.
	</p>

	<h6 class="text-secondary mb-2">1 &middot; Sending method</h6>
	<div class="form-group">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>Sending method</mat-label>
				<mat-select formControlName="config_mail_smtp_auth" (selectionChange)="onAuthMethodChange()">
					<mat-option value="login">Username and password (legacy SMTP)</mat-option>
					<mat-option value="oauth_microsoft">Microsoft 365 / Outlook (OAuth2)</mat-option>
					<mat-option value="oauth_google">Google Workspace / Gmail (OAuth2)</mat-option>
				</mat-select>
				<mat-hint>OAuth2 replaces the password only — host, port and mailbox stay the same.</mat-hint>
			</mat-form-field>
		</div>
	</div>

	<ng-container *ngIf="isOauth">
		<h6 class="text-secondary mt-4 mb-2">2 &middot; {{ providerLabel }} sign-in</h6>
		<div class="col-sm-10">

			<div class="text-center my-3" *ngIf="oauthStatusLoading">
				<mat-progress-spinner diameter="32" mode="indeterminate" color="accent"></mat-progress-spinner>
			</div>

			<p class="text-muted small mb-3" *ngIf="oauthStatusUnavailable && !oauthStatusLoading">
				This API cannot report the sign-in prerequisites yet — press Sign in and the server will name whatever is missing.
			</p>

			<ul class="list-unstyled mb-3" *ngIf="oauthStatus && !oauthStatusLoading">
				<li *ngIf="oauthStatus.tenant_required" class="mb-1">
					<i class="material-icons align-middle" aria-hidden="true"
						[class.text-success]="oauthStatus.tenant_saved" [class.text-danger]="!oauthStatus.tenant_saved">{{ oauthStatus.tenant_saved ? 'check_circle' : 'cancel' }}</i>
					<span class="align-middle ml-1">Azure tenant ID saved</span>
				</li>
				<li class="mb-1">
					<i class="material-icons align-middle" aria-hidden="true"
						[class.text-success]="oauthStatus.client_id_saved" [class.text-danger]="!oauthStatus.client_id_saved">{{ oauthStatus.client_id_saved ? 'check_circle' : 'cancel' }}</i>
					<span class="align-middle ml-1">Client ID saved <em class="text-muted" *ngIf="!oauthStatus.client_id_saved">— enter it below and press Save first</em></span>
				</li>
				<li class="mb-1">
					<i class="material-icons align-middle" aria-hidden="true"
						[class.text-success]="oauthStatus.client_secret_set" [class.text-danger]="!oauthStatus.client_secret_set">{{ oauthStatus.client_secret_set ? 'check_circle' : 'cancel' }}</i>
					<span class="align-middle ml-1">Client secret present on the server
						<em class="text-muted" *ngIf="!oauthStatus.client_secret_set">— set env <code>{{ oauthStatus.client_secret_env }}</code> for the API process</em></span>
				</li>
				<li class="mb-1">
					<i class="material-icons align-middle" aria-hidden="true"
						[class.text-success]="oauthStatus.keyfile_ok" [class.text-danger]="!oauthStatus.keyfile_ok">{{ oauthStatus.keyfile_ok ? 'check_circle' : 'cancel' }}</i>
					<span class="align-middle ml-1">Token key file readable <code class="text-muted">{{ oauthStatus.keyfile_path }}</code></span>
				</li>
				<li class="mb-1">
					<i class="material-icons align-middle" aria-hidden="true"
						[class.text-success]="oauthStatus.token_decrypts" [class.text-warning]="!oauthStatus.token_decrypts">{{ oauthStatus.token_decrypts ? 'check_circle' : 'radio_button_unchecked' }}</i>
					<span class="align-middle ml-1">
						<ng-container *ngIf="oauthStatus.token_decrypts">Mailbox authorised — this mailbox can send</ng-container>
						<ng-container *ngIf="!oauthStatus.token_decrypts && oauthStatus.token_stored">A token is stored but will not decrypt with the current key file — sign in again</ng-container>
						<ng-container *ngIf="!oauthStatus.token_stored">Mailbox not authorised yet — press Sign in below</ng-container>
					</span>
				</li>
			</ul>

			<div class="form-group" *ngIf="isMicrosoftOauth">
				<mat-form-field appearance="fill">
					<mat-label>Azure AD tenant ID (or domain)</mat-label>
					<input matInput formControlName="config_mail_oauth_tenant_id" autocomplete="off">
					<mat-hint>Azure portal &rarr; Microsoft Entra ID &rarr; Overview &rarr; Tenant ID.</mat-hint>
				</mat-form-field>
			</div>

			<div class="form-group">
				<mat-form-field appearance="fill">
					<mat-label>{{ isGoogleOauth ? 'Google OAuth client ID' : 'Application (client) ID' }}</mat-label>
					<input matInput formControlName="config_mail_oauth_client_id" autocomplete="off">
					<mat-hint *ngIf="isGoogleOauth">Google Cloud Console &rarr; Credentials &rarr; OAuth client ID, type Web application.</mat-hint>
					<mat-hint *ngIf="isMicrosoftOauth">Azure portal &rarr; App registrations &rarr; your app &rarr; Application (client) ID.</mat-hint>
				</mat-form-field>
			</div>

			<div class="mb-3" *ngIf="oauthStatus?.redirect_uri">
				<p class="text-muted small mb-1">Register this exact redirect URI with {{ providerLabel }}:</p>
				<div class="d-flex align-items-center flex-wrap gap-2">
					<code class="small">{{ oauthStatus.redirect_uri }}</code>
					<button mat-icon-button type="button" (click)="copyRedirectUri()" aria-label="Copy redirect URI">
						<mat-icon>{{ redirectCopied ? 'check' : 'content_copy' }}</mat-icon>
					</button>
				</div>
			</div>

			<button mat-flat-button color="accent" type="button" [disabled]="!prereqsOk || signingIn"
				(click)="startOAuth(smtpAuth)">
				{{ signingIn ? 'Opening ' + providerLabel + '…' : 'Sign in with ' + providerLabel }}
			</button>
			<span class="text-muted small ml-2" *ngIf="!prereqsOk && !oauthStatusLoading && oauthStatus">Clear the red items above first.</span>

			<p class="text-danger small mt-2 mb-0" *ngIf="oauthError">{{ oauthError }}</p>

			<mat-expansion-panel class="mt-3">
				<mat-expansion-panel-header>
					<mat-panel-title>Advanced — paste a refresh token manually</mat-panel-title>
				</mat-expansion-panel-header>
				<mat-form-field appearance="fill" class="w-100">
					<mat-label>Refresh token</mat-label>
					<input matInput formControlName="config_mail_oauth_refresh_token" autocomplete="off">
					<mat-hint>Normally written by Sign in. Anything pasted here is encrypted on save.</mat-hint>
				</mat-form-field>
			</mat-expansion-panel>
		</div>
	</ng-container>

	<h6 class="text-secondary mt-4 mb-2">{{ isOauth ? '3' : '2' }} &middot; Mailbox connection</h6>
	<div class="form-group">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>SMTP Username</mat-label>
				<input matInput formControlName="config_mail_smtp_username">
				<mat-hint *ngIf="isOauth">The mailbox you sign in as, and the address mail is sent from.</mat-hint>
			</mat-form-field>
		</div>
	</div>
	<div class="form-group" *ngIf="!isOauth">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>SMTP Password</mat-label>
				<input matInput type="password" formControlName="config_mail_smtp_password">
			</mat-form-field>
		</div>
	</div>
	<div class="form-group">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>SMTP Hostname</mat-label>
				<input matInput formControlName="config_mail_smtp_hostname">
			</mat-form-field>
		</div>
	</div>
	<div class="form-group">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>SMTP Port</mat-label>
				<input matInput formControlName="config_mail_smtp_port">
			</mat-form-field>
		</div>
	</div>
	<div class="form-group">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>SMTP Timeout</mat-label>
				<input matInput formControlName="config_mail_smtp_timeout">
			</mat-form-field>
		</div>
	</div>

	<h6 class="text-secondary mt-4 mb-2">{{ isOauth ? '4' : '3' }} &middot; Alerts</h6>
	<div class="form-group">
		<div class="col-sm-10">
			<mat-form-field appearance="fill">
				<mat-label>Additional Alert Mail</mat-label>
				<input matInput formControlName="config_mail_alert_email">
			</mat-form-field>
		</div>
	</div>

	<div class="col-sm-10 mt-3">
		<mat-expansion-panel>
			<mat-expansion-panel-header>
				<mat-panel-title>Advanced — legacy PHP mail() settings</mat-panel-title>
			</mat-expansion-panel-header>
			<p class="text-muted small">Only relevant when the host has disabled SMTP. Leave Mail Engine as <code>smtp</code> otherwise.</p>
			<mat-form-field appearance="fill" class="w-100">
				<mat-label>Mail Engine</mat-label>
				<input matInput formControlName="config_mail_engine">
			</mat-form-field>
			<mat-form-field appearance="fill" class="w-100">
				<mat-label>Mail Parameters</mat-label>
				<input matInput formControlName="config_mail_parameter">
				<mat-hint>Used by PHP mail() only, e.g. -f email&#64;storeaddress.com</mat-hint>
			</mat-form-field>
		</mat-expansion-panel>
	</div>

</form>

<div class="mt-4 pt-3 border-top" [formGroup]="vendorDiscForm">
	<h5 class="mb-2 text-secondary">Vendor invoice discrepancy notifications</h5>
	<p class="text-muted small mb-3">PQV alerts on quantity variance. PPV alerts when the invoice unit differs from the PO line (or there is no PO line price); invoice matching the PO but differing from standard cost does not trigger PPV in this email. Enable “Invoice unit ≠ PO line price” for PO-only mismatches. Styling matches receiving discrepancy report emails.</p>
	<div class="d-flex flex-wrap align-items-center gap-3 mb-3">
		<mat-slide-toggle formControlName="enabled" color="primary">Enable emails</mat-slide-toggle>
		<button mat-stroked-button color="primary" type="button" (click)="saveVendorDiscSettings()">
			<mat-icon>save</mat-icon> Save notification settings
		</button>
	</div>
	<mat-form-field appearance="outline" class="w-100 mb-2">
		<mat-label>Recipients</mat-label>
		<input matInput formControlName="recipients" placeholder="buyer@company.com, ap@company.com" autocomplete="off">
		<mat-hint>Comma or semicolon separated. Uses mail settings above.</mat-hint>
	</mat-form-field>
	<div class="d-flex flex-wrap gap-4 mb-2">
		<mat-checkbox formControlName="notifyInvoiceVsPo" color="primary">Invoice unit ≠ PO line price</mat-checkbox>
		<mat-checkbox formControlName="notifyPpv" color="primary">Purchase price variance (PPV)</mat-checkbox>
		<mat-checkbox formControlName="notifyPqv" color="primary">Purchase qty variance (PQV)</mat-checkbox>
	</div>
</div>
